import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../utils/LanguageContext';
import { paymentMethods } from '../../utils/data';
import { ROUTES } from '../../routes/path';
import api from '../../services/api';
import '../../styles/checkout.css';

export default function Payment() {
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [orderData, setOrderData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Get order data from previous step
        const savedOrder = sessionStorage.getItem('orderReview');
        if (!savedOrder) {
            // If no order data, redirect back to cart
            navigate(`/${ROUTES.CART}`);
            return;
        }
        setOrderData(JSON.parse(savedOrder));
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!paymentMethod) {
            setError(t('check_err_payment') || 'Please select a payment method');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Call backend checkout endpoint
            const response = await api.post('checkout/', {
                payment_method: paymentMethod
            });

            if (response.status === 200) {
                // Store order confirmation data
                const confirmationData = {
                    ...orderData,
                    paymentMethod,
                    orderId: `NOVDA-${response.data.order_id || Date.now()}`,
                    orderDate: new Date().toISOString()
                };

                sessionStorage.setItem('lastOrder', JSON.stringify(confirmationData));
                sessionStorage.removeItem('orderReview'); // Clean up

                // Navigate to success page
                navigate(`/${ROUTES.ORDER_CONFIRMATION}`);
            }
        } catch (err) {
            console.error('Checkout failed:', err);
            setError(err.response?.data?.error || 'Payment failed. Please try again.');
            setSubmitting(false);
        }
    };

    if (!orderData) {
        return (
            <div className="checkout-container">
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🌳</div>
                    <h2>{t('pay_loading')}</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="checkout-wrapper">
                {/* Payment Section */}
                <div className="checkout-form-section">
                    <div className="checkout-header">
                        <h1 className="checkout-title">{t('pay_title')}</h1>
                        <p className="checkout-subtitle">
                            {t('pay_subtitle')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="checkout-form">
                        {/* Payment Method Selection */}
                        <div className="form-card">
                            <h3 className="card-title">
                                <i className="fas fa-credit-card"></i> {t('check_payment_title')}
                            </h3>

                            <div className="payment-methods">
                                {paymentMethods.map(method => (
                                    <div
                                        key={method.id}
                                        className={`payment-method ${paymentMethod === method.id ? 'active' : ''}`}
                                        onClick={() => {
                                            setPaymentMethod(method.id);
                                            setError('');
                                        }}
                                    >
                                        <div className="payment-icon">{method.icon}</div>
                                        <div className="payment-name">{method.name}</div>
                                    </div>
                                ))}
                            </div>
                            {error && (
                                <div className="error-message" style={{ marginTop: '16px' }}>
                                    <i className="fas fa-exclamation-circle"></i> {error}
                                </div>
                            )}
                        </div>

                        {/* Order Summary for Payment */}
                        <div className="form-card">
                            <h3 className="card-title">
                                <i className="fas fa-file-invoice-dollar"></i> {t('pay_order_summary')}
                            </h3>

                            <div className="payment-summary">
                                <div className="summary-row">
                                    <span>{t('pay_subtotal')}</span>
                                    <span>{orderData.subtotal.toLocaleString()} UZS</span>
                                </div>

                                {orderData.totalDiscount > 0 && (
                                    <div className="summary-row discount">
                                        <span>{t('pay_discount')}</span>
                                        <span>-{orderData.totalDiscount.toLocaleString()} UZS</span>
                                    </div>
                                )}

                                <div className="summary-divider"></div>

                                <div className="summary-row total">
                                    <span>{t('pay_total_amount')}</span>
                                    <span className="total-amount">{orderData.total.toLocaleString()} UZS</span>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn-back"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                <i className="fas fa-arrow-left"></i>
                                {t('pay_back')}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !paymentMethod}
                                className={`btn-submit ${submitting ? 'loading' : ''}`}
                            >
                                {submitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        {t('pay_processing')}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-lock"></i>
                                        {t('pay_complete')} - {orderData.total.toLocaleString()} UZS
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Summary Sidebar */}
                <div className="checkout-summary-section">
                    <div className="summary-card sticky">
                        <h3 className="summary-title">
                            <i className="fas fa-shopping-bag"></i>
                            {t('pay_your_order')}
                        </h3>

                        <div className="total-trees-badge">
                            <i className="fas fa-tree"></i>
                            <span>{t('pay_total_trees')}: <strong>{orderData.cart.reduce((sum, item) => sum + item.count, 0)}</strong></span>
                        </div>

                        <div className="order-info-compact">
                            <div className="info-row">
                                <i className="fas fa-user"></i>
                                <span>{orderData.userInfo.fullName}</span>
                            </div>
                            <div className="info-row">
                                <i className="fas fa-phone"></i>
                                <span>{orderData.userInfo.phone}</span>
                            </div>
                            <div className="info-row">
                                <i className="fas fa-map-marker-alt"></i>
                                <span>{orderData.userInfo.region}</span>
                            </div>
                        </div>

                        <div className="summary-divider"></div>

                        <div className="summary-totals">
                            <div className="summary-row">
                                <span>{t('pay_subtotal')}</span>
                                <span>{orderData.subtotal.toLocaleString()} UZS</span>
                            </div>

                            {orderData.totalDiscount > 0 && (
                                <div className="summary-row discount">
                                    <span>
                                        <i className="fas fa-tag"></i>
                                        {t('pay_discount')}
                                    </span>
                                    <span>-{orderData.totalDiscount.toLocaleString()} UZS</span>
                                </div>
                            )}

                            <div className="summary-divider"></div>

                            <div className="summary-row total">
                                <span>{t('pay_total')}</span>
                                <span className="total-amount">{orderData.total.toLocaleString()} UZS</span>
                            </div>
                        </div>

                        <div className="security-badges">
                            <div className="badge">
                                <i className="fas fa-shield-alt"></i>
                                <span>{t('pay_secure')}</span>
                            </div>
                            <div className="badge">
                                <i className="fas fa-lock"></i>
                                <span>{t('pay_ssl')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}